import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Camera, ArrowRight } from "lucide-react";

export function FooterGalleryPreview() {
  const { data: photos } = useQuery({
    queryKey: ["gallery-footer-preview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_submissions_public")
        .select("id, file_path")
        .order("created_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  const { data: _urlData } = useQuery({
    queryKey: ["footer-gallery-signed-urls", photos?.map(p => p.file_path)],
    enabled: !!photos && photos.length > 0,
    queryFn: async () => {
      const paths = photos!.map(p => p.file_path);
      const { data, error } = await supabase.storage
        .from("gallery-submissions")
        .createSignedUrls(paths, 3600);
      if (error) throw error;
      const urlMap: Record<string, string> = {};
      data?.forEach((item) => {
        if (item.signedUrl && item.path) urlMap[item.path] = item.signedUrl;
      });
      setSignedUrls(urlMap);
      return urlMap;
    },
  });

  const getPhotoUrl = (filePath: string) => {
    return signedUrls[filePath] || "";
  };

  return (
    <div className="max-w-2xl mx-auto text-center mb-12">
      <h3 className="text-2xl font-bold text-foreground mb-4">
        Community <span className="text-rainbow-gradient">Gallery</span>
      </h3>
      <p className="text-muted-foreground mb-6">
        Sugar glider lovers showing off their Go Fluff goodies!
      </p>

      {photos && photos.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
          {photos.map((p) => (
            <div key={p.id} className="aspect-square rounded-lg overflow-hidden border border-border">
              <img
                src={getPhotoUrl(p.file_path)}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground mb-6">
          <Camera className="h-5 w-5" />
          <span className="text-sm">Gallery coming soon!</span>
        </div>
      )}

      <Link
        to="/gallery"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-brand-purple to-brand-magenta text-primary-foreground font-medium hover:opacity-90 transition-opacity"
      >
        View Gallery
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
